import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Wrench, AlertCircle, CheckCircle2, ChevronRight, Plus, Search, Loader2, ArrowRight, X,
  Clock, Zap, Cog, ClipboardCheck, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const URGENCIA_BADGE = {
  alta:        { label: "ALTA",  cls: "bg-red-100 text-red-800 border-red-300" },
  media:       { label: "MÉDIA", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  baixa:       { label: "BAIXA", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  null:        { label: "—",     cls: "bg-slate-800 text-white border-slate-900" },
};

const TIPO_BADGE = {
  corretiva:   { label: "CORRETIVA",  cls: "bg-orange-100 text-orange-800", icon: Wrench },
  preventiva:  { label: "PREVENTIVA", cls: "bg-emerald-100 text-emerald-800", icon: ClipboardCheck },
  eletrica:    { label: "ELÉTRICA",   cls: "bg-yellow-100 text-yellow-800", icon: Zap },
  mecanica:    { label: "MECÂNICA",   cls: "bg-blue-100 text-blue-800", icon: Cog },
  operacional: { label: "OPERACIONAL", cls: "bg-purple-100 text-purple-800", icon: AlertCircle },
};

const STATUS_BADGE = {
  aguardando:     { label: "AGUARDANDO", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  em_atendimento: { label: "EM ATENDIMENTO", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  resolvido:      { label: "RESOLVIDO", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  finalizado:     { label: "FINALIZADO", cls: "bg-slate-200 text-slate-800 border-slate-300" },
  cancelado:      { label: "CANCELADO", cls: "bg-red-100 text-red-700 border-red-300" },
};

const PROXIMO = {
  aguardando: "em_atendimento",
  em_atendimento: "resolvido",
  resolvido: "finalizado",
};

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

function UrgenciaBadge({ urgencia }) {
  const u = URGENCIA_BADGE[urgencia || "null"];
  return <Badge className={`${u.cls} border text-[10px] font-mono`}>{u.label}</Badge>;
}

function TipoBadge({ tipo }) {
  const t = TIPO_BADGE[tipo] || TIPO_BADGE.corretiva;
  const Icon = t.icon;
  return (
    <Badge className={`${t.cls} border text-[10px] font-mono flex items-center gap-1 w-fit`}>
      <Icon size={10} /> {t.label}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status, cls: "bg-slate-100" };
  return <Badge className={`${s.cls} border text-[10px] font-mono`}>{s.label}</Badge>;
}

// ════════════════════════════════════════════════════════════════════════
//  MODAL ABRIR CHAMADO
// ════════════════════════════════════════════════════════════════════════
function AbrirChamadoModal({ open, onOpenChange, onCriado }) {
  const [busca, setBusca] = useState("");
  const [opcoes, setOpcoes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [frota, setFrota] = useState([]);
  const [eqId, setEqId] = useState("");
  const [tipo, setTipo] = useState("corretiva");
  const [descricao, setDescricao] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [previsao, setPrevisao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) {
      setBusca(""); setOpcoes([]); setCliente(null); setFrota([]); setEqId("");
      setTipo("corretiva"); setDescricao(""); setSolicitante(""); setPrevisao("");
    }
  }, [open]);

  useEffect(() => {
    if (cliente || !open) return;
    const t = setTimeout(async () => {
      if (busca.length < 2) { setOpcoes([]); return; }
      try {
        const { data } = await api.get("/tickets-ops/busca", { params: { q: busca } });
        setOpcoes(data.clientes || []);
      } catch (e) { /* */ }
    }, 300);
    return () => clearTimeout(t);
  }, [busca, cliente, open]);

  const selCliente = async (c) => {
    setCliente(c); setOpcoes([]);
    try {
      const { data } = await api.get(`/posvenda/cliente/${c.id}/frota`);
      setFrota(data.items || []);
    } catch (e) { setFrota([]); }
  };

  const criar = async () => {
    if (!cliente) { toast.error("Selecione um cliente"); return; }
    if (!descricao.trim() || descricao.length < 5) { toast.error("Descrição obrigatória (mín 5 caracteres)"); return; }
    setSalvando(true);
    try {
      const { data } = await api.post("/posvenda/chamados", {
        cliente_id: cliente.id,
        equipamento_id: eqId || null,
        tipo,
        descricao,
        solicitante_nome: solicitante,
        previsao_atendimento: previsao || null,
      });
      toast.success(`Chamado #${data.id.slice(0, 8)} aberto`);
      onOpenChange(false);
      onCriado?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-abrir-chamado">
        <DialogHeader>
          <DialogTitle>Abrir Chamado de Serviço</DialogTitle>
          <DialogDescription>
            Triagem por IA Claude será aplicada automaticamente (Fase 2 — em breve).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!cliente && (
            <div>
              <Label className="text-xs uppercase">Cliente *</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-3 text-slate-400" />
                <Input className="pl-8" placeholder="Nome ou CNPJ..." value={busca}
                  onChange={(e) => setBusca(e.target.value)} data-testid="posv-busca-cliente" autoFocus />
              </div>
              {opcoes.length > 0 && (
                <div className="border rounded mt-1 max-h-44 overflow-y-auto">
                  {opcoes.map((c) => (
                    <button key={c.id} onClick={() => selCliente(c)} data-testid={`posv-opt-${c.id}`}
                      className="w-full text-left p-2 hover:bg-orange-50 text-xs border-b last:border-0">
                      <strong>{c.nome}</strong> · <span className="font-mono text-[10px]">{c.cnpj}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {cliente && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs flex items-center justify-between">
                <strong>{cliente.nome}</strong>
                <Button size="sm" variant="ghost" onClick={() => { setCliente(null); setFrota([]); }}>Trocar</Button>
              </div>

              <div>
                <Label className="text-xs uppercase">Tipo *</Label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(TIPO_BADGE).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => setTipo(k)} data-testid={`posv-tipo-${k}`}
                      className={`px-3 py-1.5 rounded border text-xs font-medium ${tipo === k ? "bg-orange-600 text-white border-orange-700" : "bg-white text-slate-700 border-slate-300 hover:bg-orange-50"}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase">Descrição do problema *</Label>
                <Textarea value={descricao} onChange={(e) => setDescricao(e.target.value)}
                  rows={3} placeholder="Detalhe o problema (a IA usará isso para classificar urgência)..."
                  data-testid="posv-descricao" />
                <p className="text-[10px] text-slate-500 mt-1">{descricao.length} caracteres</p>
              </div>

              {frota.length > 0 && (
                <div>
                  <Label className="text-xs uppercase">Equipamento (opcional)</Label>
                  <select value={eqId} onChange={(e) => setEqId(e.target.value)} className="w-full border rounded px-2 py-1.5 text-xs" data-testid="posv-equipamento">
                    <option value="">— Selecione —</option>
                    {frota.map((eq) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.serial || `Eq ${eq.id}`} · {eq.placa || "—"} {eq.categoria ? `(${eq.categoria})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase">Solicitante</Label>
                  <Input value={solicitante} onChange={(e) => setSolicitante(e.target.value)} data-testid="posv-solicitante" />
                </div>
                <div>
                  <Label className="text-xs uppercase">Previsão</Label>
                  <Input type="datetime-local" value={previsao} onChange={(e) => setPrevisao(e.target.value)} data-testid="posv-previsao" />
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded p-2 text-[11px] text-purple-800 flex items-center gap-2">
                <Sparkles size={14} /> Triagem IA disponível na Fase 2
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {cliente && (
            <Button onClick={criar} disabled={salvando} className="bg-orange-600 hover:bg-orange-700" data-testid="posv-confirmar">
              {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
              <Plus size={14} className="mr-1" /> Abrir chamado
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  MODAL AVANÇAR STATUS
// ════════════════════════════════════════════════════════════════════════
function AvancarModal({ chamadoId, statusAtual, novoStatus, open, onOpenChange, onDone }) {
  const [mensagem, setMensagem] = useState("");
  const [resolucao, setResolucao] = useState("");
  const [pecas, setPecas] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) { setMensagem(""); setResolucao(""); setPecas(""); setSalvando(false); }
  }, [open]);

  const precisaResolucao = novoStatus === "resolvido";

  const submit = async () => {
    if (precisaResolucao && !resolucao.trim()) { toast.error("Resolução obrigatória"); return; }
    setSalvando(true);
    try {
      await api.patch(`/posvenda/chamados/${chamadoId}/status`, {
        novo_status: novoStatus, mensagem, resolucao, pecas_utilizadas: pecas,
      });
      toast.success(`Status: ${STATUS_BADGE[novoStatus]?.label}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="posv-avancar-modal">
        <DialogHeader>
          <DialogTitle>{statusAtual} → {STATUS_BADGE[novoStatus]?.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {precisaResolucao && (
            <>
              <div>
                <Label className="text-xs uppercase">Descrição da resolução *</Label>
                <Textarea value={resolucao} onChange={(e) => setResolucao(e.target.value)} rows={3} data-testid="posv-resolucao" />
              </div>
              <div>
                <Label className="text-xs uppercase">Peças utilizadas</Label>
                <Input value={pecas} onChange={(e) => setPecas(e.target.value)} data-testid="posv-pecas" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase">Observação</Label>
            <Textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={2} data-testid="posv-mensagem" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={submit} disabled={salvando} className="bg-orange-600 hover:bg-orange-700" data-testid="posv-avancar-confirm">
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  DRAWER DETALHE
// ════════════════════════════════════════════════════════════════════════
function ChamadoDrawer({ chamadoId, open, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAvancar, setModalAvancar] = useState(null);
  const podeAvancar = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);
  const podeExcluir = ["diretor", "gerente"].includes(user?.role);

  const load = useCallback(async () => {
    if (!chamadoId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/posvenda/chamados/${chamadoId}`);
      setData(d);
    } catch (e) { toast.error("Falha ao carregar"); }
    finally { setLoading(false); }
  }, [chamadoId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const proximo = data ? PROXIMO[data.status] : null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="posv-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wrench size={18} className="text-orange-600" />
            <span className="font-mono">#{chamadoId?.slice(0, 8)}</span>
            {data && <StatusBadge status={data.status} />}
            {data && <TipoBadge tipo={data.tipo} />}
            {data && <UrgenciaBadge urgencia={data.urgencia_ia} />}
          </SheetTitle>
        </SheetHeader>

        {loading && <div className="py-8 text-center"><Loader2 className="animate-spin inline" /></div>}

        {data && (
          <div className="mt-4 space-y-3">
            <Card className="p-3">
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Cliente</h4>
              <div className="text-xs">
                <div><strong>{data.cliente_nome}</strong></div>
                <div className="font-mono text-[10px] text-slate-500">{data.cliente_cnpj || "—"}</div>
                {data.solicitante_nome && <div>Solicitante: {data.solicitante_nome}</div>}
                {data.telefone_contato && <div>Tel: {data.telefone_contato}</div>}
              </div>
            </Card>

            <Card className="p-3">
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Descrição</h4>
              <p className="text-xs whitespace-pre-line">{data.descricao}</p>
            </Card>

            {(data.resolucao || data.pecas_utilizadas) && (
              <Card className="p-3 bg-emerald-50 border-l-4 border-emerald-400">
                <h4 className="text-xs uppercase text-emerald-700 font-semibold mb-1">Resolução</h4>
                <p className="text-xs whitespace-pre-line">{data.resolucao}</p>
                {data.pecas_utilizadas && (
                  <p className="text-[10px] text-emerald-700 mt-2"><strong>Peças:</strong> {data.pecas_utilizadas}</p>
                )}
                {data.resolvido_por_nome && (
                  <p className="text-[10px] text-slate-500 mt-1">por {data.resolvido_por_nome} em {fmtData(data.resolvido_em)}</p>
                )}
              </Card>
            )}

            <Card className="p-3">
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Timeline</h4>
              <div className="space-y-1">
                {(data.timeline || []).map((u, i) => (
                  <div key={i} className="flex gap-2 text-[11px] border-l-2 border-slate-200 pl-2">
                    <span className="text-slate-400 font-mono w-24 shrink-0">{fmtData(u.data)}</span>
                    <span className="flex-1">
                      <strong>{u.status_novo}</strong>{u.user_nome && <span className="text-slate-500"> · {u.user_nome}</span>}
                      {u.observacao && <div className="italic text-slate-600">{u.observacao}</div>}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {podeAvancar && proximo && (
              <div className="flex gap-2 sticky bottom-0 bg-white pt-3 border-t">
                <Button className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => setModalAvancar(proximo)} data-testid="posv-btn-proximo">
                  <ArrowRight size={14} className="mr-1" /> Avançar para {STATUS_BADGE[proximo]?.label}
                </Button>
              </div>
            )}

            {podeExcluir && (
              <Button variant="outline" size="sm" className="text-red-700 border-red-300"
                onClick={async () => {
                  if (!window.confirm("Excluir este chamado?")) return;
                  try {
                    await api.delete(`/posvenda/chamados/${chamadoId}`);
                    toast.success("Excluído");
                    onClose(); onChanged?.();
                  } catch (e) { toast.error("Falha"); }
                }}
                data-testid="posv-btn-excluir">
                <X size={12} className="mr-1" /> Excluir chamado
              </Button>
            )}
          </div>
        )}

        <AvancarModal
          chamadoId={chamadoId} statusAtual={data?.status || "—"} novoStatus={modalAvancar}
          open={!!modalAvancar} onOpenChange={(v) => !v && setModalAvancar(null)}
          onDone={() => { load(); onChanged?.(); }}
        />
      </SheetContent>
    </Sheet>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  TAB ABERTOS
// ════════════════════════════════════════════════════════════════════════
function AbertosTab({ refreshKey, onAbrir }) {
  const [data, setData] = useState({ items: [], total: 0, resumo: {} });
  const [loading, setLoading] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/posvenda/chamados/abertos", {
        params: tipoFiltro ? { tipo: tipoFiltro } : {},
      });
      setData(d);
    } catch (e) { toast.error("Falha"); }
    finally { setLoading(false); }
  }, [tipoFiltro]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} className="border rounded px-3 py-1.5 text-xs" data-testid="posv-filtro-tipo">
          <option value="">Todos os tipos</option>
          <option value="corretiva">Corretiva</option>
          <option value="preventiva">Preventiva</option>
          <option value="eletrica">Elétrica</option>
          <option value="mecanica">Mecânica</option>
          <option value="operacional">Operacional</option>
        </select>
        <div className="flex gap-1 text-[10px] ml-auto">
          <span className="px-2 py-1 rounded bg-red-100 text-red-700">Alta {data.resumo.alta || 0}</span>
          <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">Média {data.resumo.media || 0}</span>
          <span className="px-2 py-1 rounded bg-slate-200 text-slate-700">Baixa {data.resumo.baixa || 0}</span>
          <span className="px-2 py-1 rounded bg-slate-800 text-white">— {data.resumo.sem_triagem || 0}</span>
        </div>
      </div>

      <Card data-testid="posv-tabela-abertos">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">TIPO</TableHead>
              <TableHead>DESCRIÇÃO</TableHead>
              <TableHead className="w-28">URGÊNCIA</TableHead>
              <TableHead className="w-36">STATUS</TableHead>
              <TableHead className="w-32">DATA</TableHead>
              <TableHead className="text-right">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="animate-spin inline" /></TableCell></TableRow>}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Nenhum chamado aberto.</TableCell></TableRow>
            )}
            {data.items.map((c) => (
              <TableRow key={c.id} data-testid={`posv-row-${c.id}`}>
                <TableCell><TipoBadge tipo={c.tipo} /></TableCell>
                <TableCell className="text-xs">
                  <div className="font-medium">{c.cliente_nome}</div>
                  <div className="text-slate-500 line-clamp-1">{c.descricao}</div>
                </TableCell>
                <TableCell><UrgenciaBadge urgencia={c.urgencia_ia} /></TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="text-[11px] text-slate-500">{fmtData(c.criado_em)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => onAbrir(c.id)} data-testid={`posv-detalhes-${c.id}`}>
                    <ChevronRight size={12} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  TAB FINALIZADOS
// ════════════════════════════════════════════════════════════════════════
function FinalizadosTab({ refreshKey, onAbrir }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 25;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/posvenda/chamados/finalizados", {
        params: { limit, offset: page * limit },
      });
      setData(d);
    } catch (e) { toast.error("Falha"); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  return (
    <Card data-testid="posv-tabela-finalizados">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Nº</TableHead>
            <TableHead>CLIENTE</TableHead>
            <TableHead className="w-32">TIPO</TableHead>
            <TableHead className="w-24">URGÊNCIA</TableHead>
            <TableHead className="w-40">FINALIZADO</TableHead>
            <TableHead>RESOLUÇÃO</TableHead>
            <TableHead className="text-right">AÇÕES</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="animate-spin inline" /></TableCell></TableRow>}
          {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">—</TableCell></TableRow>}
          {data.items.map((c) => (
            <TableRow key={c.id} data-testid={`posv-fin-${c.id}`}>
              <TableCell className="font-mono text-[11px]">#{c.id.slice(0, 6)}</TableCell>
              <TableCell className="text-xs">{c.cliente_nome}</TableCell>
              <TableCell><TipoBadge tipo={c.tipo} /></TableCell>
              <TableCell><UrgenciaBadge urgencia={c.urgencia_ia} /></TableCell>
              <TableCell className="text-[11px]">{fmtData(c.finalizado_em)}</TableCell>
              <TableCell className="text-xs line-clamp-1">{c.resolucao || "—"}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => onAbrir(c.id)} data-testid={`posv-fin-det-${c.id}`}>
                  <ChevronRight size={12} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.total > limit && (
        <div className="p-3 flex items-center justify-between text-xs border-t">
          <span>{data.total} chamados</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Anterior</Button>
            <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════
export default function Posvenda() {
  const { user } = useAuth();
  const [drawerId, setDrawerId] = useState(null);
  const [abrirOpen, setAbrirOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const podeAbrir = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);

  return (
    <div className="space-y-5" data-testid="posvenda-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Wrench size={28} className="text-orange-600" />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Pós-venda LAB
            </h1>
            <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
              <Sparkles size={12} className="text-purple-600" />
              Triagem por IA Claude · ordenados por urgência
            </p>
          </div>
        </div>
        {podeAbrir && (
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setAbrirOpen(true)} data-testid="posv-btn-abrir">
            <Plus size={14} className="mr-1" /> Abrir Chamado
          </Button>
        )}
      </div>

      <Tabs defaultValue="abertos" className="space-y-4">
        <TabsList data-testid="posv-tabs">
          <TabsTrigger value="abertos" data-testid="posv-tab-abertos"><Clock size={14} className="mr-1" /> Chamados Abertos</TabsTrigger>
          <TabsTrigger value="finalizados" data-testid="posv-tab-finalizados"><CheckCircle2 size={14} className="mr-1" /> Chamados Finalizados</TabsTrigger>
          <TabsTrigger value="preventivas" disabled data-testid="posv-tab-preventivas"><ClipboardCheck size={14} className="mr-1" /> Preventivas (Fase 3)</TabsTrigger>
          <TabsTrigger value="pesquisa" disabled data-testid="posv-tab-pesquisa"><Sparkles size={14} className="mr-1" /> Pesquisa (Fase 2)</TabsTrigger>
        </TabsList>

        <TabsContent value="abertos" className="m-0">
          <AbertosTab refreshKey={refreshKey} onAbrir={setDrawerId} />
        </TabsContent>
        <TabsContent value="finalizados" className="m-0">
          <FinalizadosTab refreshKey={refreshKey} onAbrir={setDrawerId} />
        </TabsContent>
      </Tabs>

      <ChamadoDrawer
        chamadoId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      <AbrirChamadoModal
        open={abrirOpen} onOpenChange={setAbrirOpen}
        onCriado={(c) => { setRefreshKey((k) => k + 1); setDrawerId(c.id); }}
      />
    </div>
  );
}
